import React from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

const size = {
    xsmall: 32,
    small: 36,
    medium: 40,
    large: 44,
};

export const Default = styled.button`
    padding: 0;
    width: ${(props) => (size[props.size] ? `${size[props.size]}px` : '44px')};
    height: ${(props) => (size[props.size] ? `${size[props.size]}px` : '44px')};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    transition-duration: 0.4s;
    border: none;

    & > svg {
        width: ${(props) => (props.svgProps?.size ? `${props.svgProps.size}px` : '18px')};
        height: ${(props) => (props.svgProps?.size ? `${props.svgProps.size}px` : '18px')};
    }

    &:disabled {
        fill: var(--gray700);
        cursor: initial;
    }

    /** Fallback for browser without focus-visible */
    &:focus {
        outline: none;
        box-shadow: var(--outline);
    }

    /** Remove mouse-focus for browser that support functionality */
    &:focus:not(:focus-visible) {
        box-shadow: none;
    }

    &:focus-visible {
        box-shadow: var(--outline);
    }

    &::-moz-focus-inner,
    &::-moz-focus-outer {
        border: 0;
    }

    &:not(:disabled):active {
        box-shadow: var(--shadow-2);
    }
`;

export const Primary = styled(Default)`
    fill: var(--color-white);
    background-color: var(--blue500);

    &:not(:disabled):hover {
        background-color: var(--blue700);
    }

    &:disabled {
        background-color: var(--gray200);
    }

    &:not(:disabled):active {
        background-color: var(--blue400);
    }
`;

export const Secondary = styled(Default)`
    background-color: var(--color-white);
    border: 2px solid var(--blue500);
    fill: var(--blue500);

    &:not(:disabled):hover {
        border-color: var(--blue700);
        fill: var(--blue700);
    }

    &:disabled {
        border-color: var(--gray200);
    }

    &:not(:disabled):active {
        border-color: var(--blue400);
        fill: var(--blue400);
    }
`;

export const Tertiary = styled(Default)`
    fill: var(--blue500);
    background-color: transparent;

    &:not(:disabled):hover {
        background-color: var(--blue50);
    }

    &:focus {
        background-color: var(--color-white);
    }

    &:not(:disabled):active {
        background-color: var(--color-white);
    }
`;

const IconButton = ({ type = 'primary', size = 'large', disabled = false, onClick, children, svgProps, ariaLabel }) => {
    switch (type) {
        case 'tertiary':
            return (
                <Tertiary size={size} disabled={disabled} onClick={onClick} svgProps={svgProps} aria-label={ariaLabel}>
                    {children}
                </Tertiary>
            );
        case 'secondary':
            return (
                <Secondary size={size} disabled={disabled} onClick={onClick} aria-label={ariaLabel}>
                    {children}
                </Secondary>
            );
        case 'primary':
        default:
            return (
                <Primary size={size} disabled={disabled} onClick={onClick} aria-label={ariaLabel}>
                    {children}
                </Primary>
            );
    }
};

IconButton.propTypes = {
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
    size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    svgProps: PropTypes.object,
    children: PropTypes.node,
    ariaLabel: PropTypes.string,
};

export default IconButton;
